import { css } from 'aphrodite';
import * as React from 'react';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import type { ISupportThread } from '../../../models/Support/ISupportThread';
import type { ISupportTicketEvent } from '../../../models/Support/ISupportTicketEvent';
import { ThreadStatus, ThreadType, TicketStatus } from '../../../models/Support/SupportEnums';
import { useSupportMailboxQuery } from '../../../queries/Support/useSupportMailboxQuery';
import {
	useMarkNotificationsReadMutation,
	useSupportNotificationsUnreadCountQuery,
} from '../../../queries/Support/useSupportNotifications';
import { useSupportTeamPresentation } from '../../../queries/Support/useSupportTeam';
import { useUpdateThreadStatusMutation } from '../../../queries/Support/useSupportThreadMutations';
import { useSupportThreadsQuery } from '../../../queries/Support/useSupportThreadsQuery';
import { useSupportTicketEventsQuery } from '../../../queries/Support/useSupportTicketEvents';
import {
	useAssignTicketMutation,
	useUpdateTicketStatusMutation,
} from '../../../queries/Support/useSupportTicketMutations';
import { useSupportTicketQuery } from '../../../queries/Support/useSupportTicketQuery';
import { getApiErrorMessage } from '../../../utils/Support/supportHelpers';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { EventItem } from './EventItem';
import { ThreadItem } from './ThreadItem';
import { ComposerMode, TicketComposer, TicketComposerHandle } from './TicketComposer';
import { ComposerErrorBanner } from './TicketComposer/ComposerErrorBanner';
import { getComposerDraftStorageKey, readStoredComposerDraft } from './TicketComposer/useComposerDraft';
import './styles/threadBodyStyles.css';
import { conversationPaneStyles as local } from './styles/conversationPaneStyles';
import { inboxStyles as s } from './styles/inboxStyles';
import { useSupportTicketActivityVisibility } from './useSupportTicketActivityVisibility';

/** Local draft-status resolver for AI draft thread display. */
const resolveDraftStatus = (
	thread: ISupportThread,
	pin: { threadId: string; status: 'accepted' | 'rejected' } | null
): 'pending' | 'accepted' | 'rejected' => {
	if (pin && pin.threadId === thread.id) return pin.status;
	if (thread.status === ThreadStatus.Accepted) return 'accepted';
	if (thread.status === ThreadStatus.Rejected) return 'rejected';
	return 'pending';
};

interface ConversationPaneProps {
	ticketId: string;
	/**
	 * Increments each time the user picks "Forward" from the ticket bar (which now lives in the
	 * page masthead, outside this pane). We watch the counter and open the composer in forward
	 * mode — the composer state lives here, so the action is signalled rather than handled upstream.
	 */
	forwardSignal?: number;
	targetThreadId?: string;
	/** Opens the customer picker for customerless tickets. */
	onChangeCustomer?: () => void;
	/** Called after "Send & close" closes the ticket — parent advances to the next ticket. */
	onTicketClosed?: () => void;
}

type TimelineItem =
	| { kind: 'thread'; sortKey: number; data: ISupportThread }
	| { kind: 'event'; sortKey: number; data: ISupportTicketEvent };

function getLatestThreadId(timeline: TimelineItem[]) {
	for (let index = timeline.length - 1; index >= 0; index--) {
		const item = timeline[index];
		if (item.kind === 'thread') return item.data.id;
	}
	return undefined;
}

/**
 * Pane 2 — the conversation. Self-contained: it fetches the selected ticket's threads/events and
 * composes the thread/event/composer pieces used by the triple-panel inbox.
 */
export const ConversationPane: React.FC<ConversationPaneProps> = ({
	ticketId,
	forwardSignal,
	targetThreadId,
	onChangeCustomer,
	onTicketClosed,
}) => {
	const userSession = useAdminUserSession();
	const currentUser = userSession.user;
	const currentUserName = [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ');

	// A ticket with a stored draft should reopen the composer to that draft's mode/type instead of
	// the collapsed default — the composer only seeds the draft's *content*, so mode/type must be
	// driven here in one place (previously a second restore effect raced this and always lost).
	const draftComposerState = (id: string) => {
		const draft = readStoredComposerDraft(getComposerDraftStorageKey(currentUser.id, id));
		return {
			mode: (draft ? 'inline' : 'collapsed') as ComposerMode,
			replyType: draft?.replyType ?? 'reply',
			forwardThreadId: draft?.replyType === 'forward' ? draft.forwardThreadId : undefined,
		};
	};

	const [composerMode, setComposerMode] = React.useState<ComposerMode>(() => draftComposerState(ticketId).mode);
	const [replyType, setReplyType] = React.useState<'reply' | 'note' | 'forward'>(
		() => draftComposerState(ticketId).replyType
	);
	const [forwardThreadId, setForwardThreadId] = React.useState<string | undefined>(
		() => draftComposerState(ticketId).forwardThreadId
	);
	const [composerHasContent, setComposerHasContent] = React.useState(false);
	const [showTicketActivity] = useSupportTicketActivityVisibility();
	const [actionError, setActionError] = React.useState<string | null>(null);
	const [draftStatusPin, setDraftStatusPin] = React.useState<{
		threadId: string;
		status: 'accepted' | 'rejected';
	} | null>(null);
	const composerRef = React.useRef<TicketComposerHandle>(null);
	const hasMountedRef = React.useRef(false);

	// Reset transient composer/draft state whenever the selected ticket changes, seeding the composer
	// mode/type from the target ticket's stored draft so a saved reply reopens instead of collapsing.
	React.useEffect(() => {
		if (!hasMountedRef.current) {
			hasMountedRef.current = true;
			return;
		}
		const next = draftComposerState(ticketId);
		setComposerMode(next.mode);
		setReplyType(next.replyType);
		setForwardThreadId(next.forwardThreadId);
		setComposerHasContent(false);
		setActionError(null);
		setDraftStatusPin(null);
		// draftComposerState is a stable local reader keyed only on userSession/ticketId.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ticketId, currentUser.id]);

	const pollingInterval: number | false = composerHasContent ? false : 10_000;
	const { data: ticket, isLoading: ticketLoading } = useSupportTicketQuery(ticketId, {
		refetchInterval: pollingInterval,
	});
	const { data: threads, isLoading: threadsLoading } = useSupportThreadsQuery(ticketId, {
		refetchInterval: pollingInterval,
	});
	const { data: events, isError: eventsFailed } = useSupportTicketEventsQuery(ticketId, {
		refetchInterval: pollingInterval,
	});
	const { data: mailbox } = useSupportMailboxQuery(ticket?.mailboxId || '');
	const { agentNameMap, agentProfilePicMap, agentProfilePicEmailMap } = useSupportTeamPresentation(
		ticket?.mailboxId || ''
	);
	const sendingDisabled = mailbox?.sendingEnabled === false;

	const updateThreadStatus = useUpdateThreadStatusMutation(ticketId);
	const updateTicketStatus = useUpdateTicketStatusMutation(ticketId);
	const assignTicket = useAssignTicketMutation(ticketId);

	const { data: unreadNotificationCount } = useSupportNotificationsUnreadCountQuery();
	const markTicketNotificationsRead = useMarkNotificationsReadMutation();
	const markTicketNotificationsReadMutate = markTicketNotificationsRead.mutate;
	React.useEffect(() => {
		if ((unreadNotificationCount ?? 0) > 0) markTicketNotificationsReadMutate(ticketId);
		// Unread-count polling must not retrigger marking the selected ticket.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ticketId, markTicketNotificationsReadMutate]);

	const aiDraftThread = threads?.values?.find(t => t.type === ThreadType.AiDraft);
	// Drop the pin once it no longer matches the live draft, or the server has caught up.
	React.useEffect(() => {
		if (!draftStatusPin) return;
		if (aiDraftThread?.id !== draftStatusPin.threadId) {
			setDraftStatusPin(null);
			return;
		}
		const settled =
			(draftStatusPin.status === 'accepted' && aiDraftThread.status === ThreadStatus.Accepted) ||
			(draftStatusPin.status === 'rejected' && aiDraftThread.status === ThreadStatus.Rejected);
		if (settled) setDraftStatusPin(null);
	}, [aiDraftThread, draftStatusPin]);

	const handleAcceptDraft = (body: string, threadId?: string) => {
		composerRef.current?.setContent(body);
		setReplyType('reply');
		setComposerMode('inline');
		if (threadId) {
			setActionError(null);
			setDraftStatusPin({ threadId, status: 'accepted' });
			updateThreadStatus.mutate(
				{ threadId, status: ThreadStatus.Accepted },
				{
					onError: error =>
						setActionError(getApiErrorMessage(error, 'Failed to accept the AI draft. Please try again.')),
				}
			);
		}
	};

	const handleRejectDraft = (threadId: string) => {
		setActionError(null);
		setDraftStatusPin({ threadId, status: 'rejected' });
		updateThreadStatus.mutate(
			{ threadId, status: ThreadStatus.Rejected },
			{
				onError: error => setActionError(getApiErrorMessage(error, 'Failed to reject the AI draft. Please try again.')),
			}
		);
	};

	const handleForward = React.useCallback(() => {
		const latestThread = threads?.values
			?.filter(t => t.type === ThreadType.CustomerMessage || t.type === ThreadType.AgentReply)
			.at(-1);
		if (latestThread) {
			setForwardThreadId(latestThread.id);
			setReplyType('forward');
			setComposerMode('inline');
		}
	}, [threads]);

	// The ticket bar (and its Forward action) lives in the page masthead; it bumps `forwardSignal`
	// to ask us to open the composer in forward mode. Track the last seen value so the initial
	// render — and unrelated re-renders — don't spuriously trigger a forward.
	const lastForwardSignalRef = React.useRef(forwardSignal);
	React.useEffect(() => {
		if (forwardSignal === undefined) return;
		if (forwardSignal === lastForwardSignalRef.current) return;
		lastForwardSignalRef.current = forwardSignal;
		handleForward();
	}, [forwardSignal, handleForward]);

	const threadValues = threads?.values;
	const eventValues = events?.values;
	const timeline = React.useMemo<TimelineItem[]>(
		() =>
			[
				...(threadValues ?? []).map(
					(t): TimelineItem => ({ kind: 'thread', sortKey: new Date(t.creationDate).getTime(), data: t })
				),
				...(showTicketActivity ? (eventValues ?? []) : []).map(
					(e): TimelineItem => ({ kind: 'event', sortKey: new Date(e.creationDate).getTime(), data: e })
				),
			].sort((a, b) => a.sortKey - b.sortKey),
		[threadValues, eventValues, showTicketActivity]
	);
	const latestThreadId = getLatestThreadId(timeline);
	const latestTimelineItem = timeline[timeline.length - 1];
	const latestTimelineItemKey = latestTimelineItem
		? `${latestTimelineItem.kind}-${latestTimelineItem.data.id}`
		: undefined;
	const threadAreaRef = React.useRef<HTMLDivElement>(null);
	const composerHostRef = React.useRef<HTMLDivElement>(null);
	const threadAreaPinnedToBottomRef = React.useRef(true);
	const composerHeightRef = React.useRef(0);
	const [scrolledTargetThreadId, setScrolledTargetThreadId] = React.useState<string | undefined>(undefined);

	const updateThreadAreaPinnedToBottom = React.useCallback(() => {
		const threadArea = threadAreaRef.current;
		if (!threadArea) return;
		const distanceFromBottom = threadArea.scrollHeight - threadArea.scrollTop - threadArea.clientHeight;
		threadAreaPinnedToBottomRef.current = distanceFromBottom <= 8;
	}, []);

	const scrollThreadAreaToBottom = React.useCallback(() => {
		const threadArea = threadAreaRef.current;
		if (!threadArea) return;
		threadArea.scrollTop = threadArea.scrollHeight;
		threadAreaPinnedToBottomRef.current = true;
	}, []);

	React.useEffect(() => {
		setScrolledTargetThreadId(undefined);
	}, [targetThreadId, ticketId]);

	React.useEffect(() => {
		if (targetThreadId || threadsLoading || !latestTimelineItemKey) return;
		const timeout = window.setTimeout(scrollThreadAreaToBottom, 0);
		return () => window.clearTimeout(timeout);
	}, [targetThreadId, ticketId, threadsLoading, latestTimelineItemKey, scrollThreadAreaToBottom]);

	React.useEffect(() => {
		if (!targetThreadId || threadsLoading || scrolledTargetThreadId === targetThreadId) return;
		const threadArea = threadAreaRef.current;
		if (!threadArea) return;
		const timeout = window.setTimeout(() => {
			const target = Array.from(threadArea.querySelectorAll<HTMLElement>('[data-thread-id]')).find(
				element => element.dataset.threadId === targetThreadId
			);
			if (!target) return;
			target.scrollIntoView?.({ block: 'center' });
			updateThreadAreaPinnedToBottom();
			setScrolledTargetThreadId(targetThreadId);
		}, 0);
		return () => window.clearTimeout(timeout);
	}, [scrolledTargetThreadId, targetThreadId, threadsLoading, timeline, updateThreadAreaPinnedToBottom]);

	React.useLayoutEffect(() => {
		const composerHost = composerHostRef.current;
		if (!composerHost || typeof window.ResizeObserver === 'undefined') return;
		composerHeightRef.current = composerHost.getBoundingClientRect().height;
		const observer = new window.ResizeObserver(entries => {
			const nextHeight = entries[0]?.contentRect.height ?? composerHost.getBoundingClientRect().height;
			const heightDelta = nextHeight - composerHeightRef.current;
			composerHeightRef.current = nextHeight;
			if (heightDelta <= 0) return;
			const threadArea = threadAreaRef.current;
			if (!threadArea) return;
			const distanceFromBottom = threadArea.scrollHeight - threadArea.scrollTop - threadArea.clientHeight;
			if (!threadAreaPinnedToBottomRef.current && distanceFromBottom > heightDelta + 8) return;
			window.requestAnimationFrame(scrollThreadAreaToBottom);
		});
		observer.observe(composerHost);
		return () => observer.disconnect();
	}, [scrollThreadAreaToBottom]);

	if (ticketLoading) {
		return (
			<div className={css(s.conversationPane)}>
				<div className={css(s.loading)}>
					<LoadingSpinner type='small' />
				</div>
			</div>
		);
	}

	if (!ticket) {
		return (
			<div className={css(s.conversationPane)}>
				<div className={css(s.empty)}>Ticket not found.</div>
			</div>
		);
	}

	return (
		<div className={css(s.conversationPane)}>
			{actionError && <ComposerErrorBanner message={actionError} onDismiss={() => setActionError(null)} />}
			<div
				className={css(local.threadArea, composerMode === 'collapsed' && local.threadAreaFloatingComposer)}
				ref={threadAreaRef}
				onScroll={updateThreadAreaPinnedToBottom}
			>
				{showTicketActivity && eventsFailed && (
					<div className={css(local.timelineNotice)} role='status'>
						Ticket activity is unavailable right now — showing messages only.
					</div>
				)}
				{threadsLoading && (
					<div className={css(s.loading)}>
						<LoadingSpinner type='small' />
					</div>
				)}
				{!threadsLoading &&
					timeline.map(item =>
						item.kind === 'thread' ? (
							<ThreadItem
								key={`thread-${item.data.id}`}
								thread={item.data}
								ticket={ticket}
								isLatestThread={item.data.id === latestThreadId}
								agentNameMap={agentNameMap}
								agentProfilePicMap={agentProfilePicMap}
								agentProfilePicEmailMap={agentProfilePicEmailMap}
								currentUserId={currentUser.id}
								currentUserName={currentUserName}
								currentUserEmail={currentUser.email}
								currentUserProfilePic={currentUser.profilePic}
								currentAccountId={userSession.account?.id}
								onAcceptDraft={handleAcceptDraft}
								onRejectDraft={handleRejectDraft}
								draftStatus={
									item.data.type === ThreadType.AiDraft ? resolveDraftStatus(item.data, draftStatusPin) : undefined
								}
							/>
						) : (
							<EventItem key={`event-${item.data.id}`} event={item.data} agentNameMap={agentNameMap} />
						)
					)}
			</div>

			<div ref={composerHostRef} className={css(local.composerHost)}>
				<TicketComposer
					key={ticketId}
					ref={composerRef}
					ticketId={ticketId}
					ticket={ticket}
					mode={composerMode}
					onModeChange={setComposerMode}
					replyType={replyType}
					onReplyTypeChange={setReplyType}
					forwardThreadId={forwardThreadId}
					onAfterSend={() => {
						setActionError(null);
						updateTicketStatus.mutate(TicketStatus.Closed, {
							onSuccess: () => onTicketClosed?.(),
							onError: error =>
								setActionError(
									getApiErrorMessage(error, 'Reply sent, but closing the ticket failed. Please try again.')
								),
						});
					}}
					onSelfAssign={() => {
						setActionError(null);
						assignTicket.mutate(currentUser.id, {
							onError: error =>
								setActionError(getApiErrorMessage(error, 'Failed to assign the ticket to you. Please try again.')),
						});
					}}
					onChangeCustomer={onChangeCustomer}
					onHasContentChange={setComposerHasContent}
					sendingDisabled={sendingDisabled}
				/>
			</div>
		</div>
	);
};
